import React, { useMemo, useState } from "react";
import {
  AppstoreOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
  EyeOutlined,
  HolderOutlined,
  LayoutOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Button, Card, Col, Empty, Input, Modal, Popover, Row, Space, Tag, Tooltip, Typography, message } from "antd";
import { useDrag, useDrop } from "react-dnd";
import { CreateButton } from "@comps/components";
import { applyWidgetToRows, getAssignedWidgetIds, LAYOUT_PRESETS } from "./customDashboardBuilderUtils";

const { Text, Title } = Typography;
const WIDGET_TYPE = "CUSTOM_DASHBOARD_WIDGET";

const builderStyles = {
  shell: {
    marginTop: 24,
  },
  panelCard: {
    borderRadius: 16,
    border: "1px solid #d7e3f4",
    boxShadow: "0 10px 28px rgba(20, 54, 96, 0.08)",
    overflow: "hidden",
  },
  panelHeader: {
    padding: "18px 20px",
    borderBottom: "1px solid #e6eef8",
    background: "linear-gradient(180deg, #f8fbff 0%, #f3f7fc 100%)",
  },
  libraryList: {
    padding: 16,
    maxHeight: 640,
    overflowY: "auto",
  },
  workspaceBody: {
    padding: 16,
    background: "#f6f9fc",
    maxHeight: 640,
    overflowY: "auto",
  },
  rowCard: {
    borderRadius: 16,
    border: "1px solid #d9e4f2",
    boxShadow: "0 6px 20px rgba(24, 62, 113, 0.06)",
  },
  rowHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  slot: {
    minHeight: 170,
    borderRadius: 14,
    border: "1px dashed #b8cbe1",
    background: "#ffffff",
    padding: 14,
    transition: "all 0.18s ease",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    height: "100%",
  },
  slotEmpty: {
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    color: "#6f7e95",
  },
  widgetCard: {
    borderRadius: 14,
    border: "1px solid #d7e3f4",
    padding: 14,
    background: "#fff",
    transition: "all 0.16s ease",
  },
};

const widthToSpan = {
  "100%": 24,
  "75%": 18,
  "50%": 12,
  "25%": 6,
  "33.33%": 8,
};

const getSlotSpan = (width) => widthToSpan[width] || 24;

const getWidgetCopy = (item) => item?.description || "Drag this widget into the layout or select a slot and click add.";

const WidgetLibraryCard = ({ item, assigned, disabled, hasSelectedSlot, onAssign }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: WIDGET_TYPE,
    item: { id: item.id },
    canDrag: !disabled,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }), [item.id, disabled]);

  return (
    <div
      ref={ drag }
      style={ {
        ...builderStyles.widgetCard,
        opacity: isDragging ? 0.45 : 1,
        borderColor: hasSelectedSlot ? "#1677ff" : (assigned ? "#9ec5ff" : "#d7e3f4"),
        background: hasSelectedSlot ? "#eff6ff" : "#fff",
        cursor: disabled ? "default" : "grab",
      } }
    >
      <Space direction="vertical" size={ 10 } style={ { width: "100%" } }>
        <Space align="start" style={ { width: "100%", justifyContent: "space-between" } }>
          <Space direction="vertical" size={ 4 } style={ { flex: 1 } }>
            <Text strong style={ { color: "#21324d" } }>{ item.name }</Text>
            <Text type="secondary" style={ { fontSize: 12 } }>
              { getWidgetCopy(item) }
            </Text>
          </Space>
          <HolderOutlined style={ { color: "#8ea0b7", fontSize: 16, marginTop: 2 } } />
        </Space>

        <Space wrap>
          { assigned && <Tag color="blue">Assigned</Tag> }
        </Space>

        {!disabled && (
          <Button
            type={ hasSelectedSlot ? "primary" : "default" }
            icon={ hasSelectedSlot ? <CheckCircleOutlined /> : <PlusOutlined /> }
            onClick={ () => onAssign(item.id) }
            block
          >
            { hasSelectedSlot ? "Replace in selected slot" : "Add to layout" }
          </Button>
        ) }
      </Space>
    </div>
  );
};

const AssignedWidgetCard = ({ widget, disabled, onClear }) => (
  <div
    style={ {
      ...builderStyles.widgetCard,
      borderColor: "#b7d4ff",
      background: "linear-gradient(180deg, #f8fbff 0%, #eef5ff 100%)",
      height: "100%",
    } }
  >
    <Space direction="vertical" size={ 12 } style={ { width: "100%" } }>
      <Space align="start" style={ { width: "100%", justifyContent: "space-between" } }>
        <Space direction="vertical" size={ 4 }>
          <Text strong style={ { color: "#183356" } }>{ widget?.name || "Assigned widget" }</Text>
          <Text type="secondary" style={ { fontSize: 12 } }>
            { getWidgetCopy(widget) }
          </Text>
        </Space>
        <Tag color="blue" style={ { marginInlineEnd: 0 } }>Assigned</Tag>
      </Space>

      {!disabled && (
        <Space wrap>
          <Button icon={ <CloseOutlined /> } onClick={ onClear }>
            Clear
          </Button>
        </Space>
      ) }
    </Space>
  </div>
);

const LayoutPresetPicker = ({ onSelect }) => (
  <Space direction="vertical" size={ 10 } style={ { width: 230 } }>
    <Text strong>Select layout</Text>
    { LAYOUT_PRESETS.map((preset) => (
      <Button key={ preset.key } onClick={ () => onSelect(preset.widths) } block>
        <Space style={ { width: "100%", justifyContent: "space-between" } }>
          <span>{ preset.label }</span>
          <Text type="secondary">{ preset.title }</Text>
        </Space>
      </Button>
    )) }
  </Space>
);

const DashboardSlot = ({
  rowIndex,
  colIndex,
  column,
  widget,
  disabled,
  selected,
  onSelectSlot,
  onDropWidget,
  onClear,
  onSplit,
  onOpenPicker,
}) => {
  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: WIDGET_TYPE,
    canDrop: () => !disabled,
    drop: (item) => {
      if (!disabled) {
        onDropWidget(item.id, colIndex, rowIndex);
      }
      return { moved: true };
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  }), [disabled, rowIndex, colIndex, onDropWidget]);

  const hasWidget = column.widgets.length > 0;
  const highlightStyle = isOver && canDrop
    ? { borderColor: "#52c41a", background: "#f6ffed", boxShadow: "0 0 0 3px rgba(82, 196, 26, 0.12)" }
    : (selected ? { borderColor: "#1677ff", boxShadow: "0 0 0 3px rgba(22, 119, 255, 0.12)" } : {});

  return (
    <Col xs={ 24 } md={ getSlotSpan(column.width) } key={ `${rowIndex}-${colIndex}` }>
      <div
        ref={ drop }
        onClick={ () => !disabled && onSelectSlot(rowIndex, colIndex) }
        style={ {
          ...builderStyles.slot,
          ...(hasWidget ? {} : builderStyles.slotEmpty),
          ...highlightStyle,
          cursor: disabled ? "default" : "pointer",
        } }
      >
        <Space align="center" style={ { width: "100%", justifyContent: "space-between" } }>
          <Space wrap size={ 8 }>
            <Tag color={ selected ? "blue" : "default" }>Slot { colIndex + 1 }</Tag>
            <Tag>{ column.width }</Tag>
          </Space>

          {!disabled && (
            <Space wrap size={ 8 }>
              {!hasWidget && (
                <>
                  <Button
                    type="primary"
                    ghost
                    icon={ <PlusOutlined /> }
                    onClick={ (event) => {
                      event.stopPropagation();
                      onOpenPicker(rowIndex, colIndex);
                    } }
                  >
                    Choose widget
                  </Button>
                  <Popover
                    placement="bottomRight"
                    trigger="click"
                    content={ <LayoutPresetPicker onSelect={ (widths) => onSplit(widths) } /> }
                  >
                    <Button icon={ <LayoutOutlined /> } onClick={ (event) => event.stopPropagation() }>
                      Split layout
                    </Button>
                  </Popover>
                </>
              )}
              {hasWidget && (
                <Button icon={ <CloseOutlined /> } onClick={ (event) => {
                  event.stopPropagation();
                  onClear();
                } }>
                  Clear
                </Button>
              )}
            </Space>
          ) }
        </Space>

        { hasWidget ? (
          <AssignedWidgetCard widget={ widget } disabled={ disabled } onClear={ onClear } />
        ) : (
          <Space direction="vertical" size={ 10 } style={ { maxWidth: 280 } }>
            <AppstoreOutlined style={ { fontSize: 24, color: selected ? "#1677ff" : "#9db0c7" } } />
            <Text strong style={ { color: "#24364f" } }>
              Drop a widget here or choose one from the library
            </Text>
            <Text type="secondary">
              { selected ? "This slot is selected. Use the widget library or the Choose widget action to place a widget here." : "Click this slot to open quick actions and pick a widget." }
            </Text>
          </Space>
        ) }
      </div>
    </Col>
  );
};

const DashboardRowCard = ({
  row,
  rowIndex,
  widgetsById,
  disabled,
  selectedSlot,
  onSelectSlot,
  onDropWidget,
  onClearWidget,
  onMoveRow,
  onRemoveRow,
  onSplitColumn,
  onOpenPicker,
}) => (
  <Card size="small" style={ builderStyles.rowCard } bodyStyle={ { padding: 16 } }>
    <Space direction="vertical" size={ 16 } style={ { width: "100%" } }>
      <div style={ builderStyles.rowHeader }>
        <Space direction="vertical" size={ 2 }>
          <Title level={ 5 } style={ { margin: 0, color: "#20324b" } }>Row { rowIndex + 1 }</Title>
          <Text type="secondary">
            { row.columns.length } { row.columns.length > 1 ? "columns" : "column" } in this row
          </Text>
        </Space>

        {!disabled && (
          <Space wrap>
            <Tooltip title="Move row up">
              <Button icon={ <ArrowUpOutlined /> } onClick={ () => onMoveRow(rowIndex, "up") } />
            </Tooltip>
            <Tooltip title="Move row down">
              <Button icon={ <ArrowDownOutlined /> } onClick={ () => onMoveRow(rowIndex, "down") } />
            </Tooltip>
            <Tooltip title="Remove row">
              <Button danger icon={ <DeleteOutlined /> } onClick={ () => onRemoveRow(rowIndex) } />
            </Tooltip>
          </Space>
        ) }
      </div>

      <Row gutter={ [12, 12] }>
        { row.columns.map((column, colIndex) => {
          const widgetId = column.widgets[0];
          const widget = widgetId ? widgetsById[widgetId] : null;
          const selected = selectedSlot?.rowIndex === rowIndex && selectedSlot?.colIndex === colIndex;

          return (
            <DashboardSlot
              key={ `${rowIndex}-${colIndex}` }
              rowIndex={ rowIndex }
              colIndex={ colIndex }
              column={ column }
              widget={ widget }
              disabled={ disabled }
              selected={ selected }
              onSelectSlot={ onSelectSlot }
              onDropWidget={ onDropWidget }
              onClear={ () => onClearWidget(rowIndex, colIndex) }
              onSplit={ (widths) => onSplitColumn(rowIndex, colIndex, widths) }
              onOpenPicker={ onOpenPicker }
            />
          );
        }) }
      </Row>
    </Space>
  </Card>
);

const getFirstEmptySlot = (rows = []) => {
  for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
    for (let colIndex = 0; colIndex < rows[rowIndex].columns.length; colIndex += 1) {
      if (!rows[rowIndex].columns[colIndex].widgets.length) {
        return { rowIndex, colIndex };
      }
    }
  }

  return null;
};

const CustomDashboardBuilder = ({
  widgets,
  rows,
  disabled = false,
  onChangeRows,
  onAddRow,
  onMoveRow,
  onRemoveRow,
  onClearWidget,
  onSplitColumn,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const widgetsById = useMemo(() => widgets.reduce((acc, widget) => {
    acc[widget.id] = widget;
    return acc;
  }, {}), [widgets]);

  const assignedWidgetIds = useMemo(() => new Set(getAssignedWidgetIds(rows)), [rows]);

  const visibleWidgets = useMemo(() => {
    const keyword = searchValue.trim().toLowerCase();
    if (!keyword) {
      return widgets;
    }

    return widgets.filter((widget) => (
      `${widget.name || ""} ${widget.description || ""}`.toLowerCase().includes(keyword)
    ));
  }, [searchValue, widgets]);

  const handleWidgetPlacement = (widgetId, colIndex, rowIndex) => {
    onChangeRows((prevRows) => applyWidgetToRows(prevRows, widgetId, rowIndex, colIndex));
    setSelectedSlot({ rowIndex, colIndex });
  };

  const handleAssignFromLibrary = (widgetId) => {
    const target = selectedSlot || getFirstEmptySlot(rows);
    if (!target) {
      message.info("All slots are filled. Select a slot to replace its widget.");
      return;
    }

    handleWidgetPlacement(widgetId, target.colIndex, target.rowIndex);
  };

  const openSlotPicker = (rowIndex, colIndex) => {
    setSelectedSlot({ rowIndex, colIndex });
    setPickerOpen(true);
  };

  const handleAssignFromPicker = (widgetId) => {
    if (!selectedSlot) {
      return;
    }

    handleWidgetPlacement(widgetId, selectedSlot.colIndex, selectedSlot.rowIndex);
    setPickerOpen(false);
  };

  const assignedCount = assignedWidgetIds.size;

  return (
    <div style={ builderStyles.shell }>
      <Row gutter={ [16, 16] } align="stretch">
        {!disabled && (
          <Col xs={ 24 } xl={ 7 }>
            <Card style={ builderStyles.panelCard } bodyStyle={ { padding: 0 } }>
              <div style={ builderStyles.panelHeader }>
                <Space direction="vertical" size={ 4 } style={ { width: "100%" } }>
                  <Space align="center" style={ { width: "100%", justifyContent: "space-between" } }>
                    <Title level={ 4 } style={ { margin: 0, color: "#20324b" } }>Widget Library</Title>
                    <Tag color="blue">{ assignedCount } / { widgets.length } assigned</Tag>
                  </Space>
                  <Text type="secondary">
                    Search widgets, then drag them into the canvas or click add for the selected slot.
                  </Text>
                </Space>
              </div>

              <div style={ { padding: 16, borderBottom: "1px solid #edf2f9" } }>
                <Input
                  allowClear
                  value={ searchValue }
                  onChange={ (event) => setSearchValue(event.target.value) }
                  placeholder="Search widgets"
                  prefix={ <SearchOutlined style={ { color: "#8ea0b7" } } /> }
                />
              </div>

              <div style={ builderStyles.libraryList }>
                <Space direction="vertical" size={ 12 } style={ { width: "100%" } }>
                  { visibleWidgets.length === 0 && (
                    <Empty image={ Empty.PRESENTED_IMAGE_SIMPLE } description="No widgets match your search." />
                  ) }
                  { visibleWidgets.map((widget) => (
                    <WidgetLibraryCard
                      key={ widget.id }
                      item={ widget }
                      assigned={ assignedWidgetIds.has(widget.id) }
                      disabled={ disabled }
                      hasSelectedSlot={ !!selectedSlot }
                      onAssign={ handleAssignFromLibrary }
                    />
                  )) }
                </Space>
              </div>
            </Card>
          </Col>
        ) }

        <Col xs={ 24 } xl={ disabled ? 24 : 17 }>
          <Card style={ builderStyles.panelCard } bodyStyle={ { padding: 0 } }>
            <div style={ builderStyles.panelHeader }>
              <Space direction="vertical" size={ 4 } style={ { width: "100%" } }>
                <Space align="center" style={ { width: "100%", justifyContent: "space-between" } }>
                  <Title level={ 4 } style={ { margin: 0, color: "#20324b" } }>Dashboard Layout</Title>
                  <Tag color={ disabled ? "default" : "green" } icon={ disabled ? <EyeOutlined /> : <LayoutOutlined /> }>
                    { disabled ? "Read only" : `${rows.length} rows configured` }
                  </Tag>
                </Space>
                <Text type="secondary">
                  { disabled
                    ? "Review the configured widgets and row layout."
                    : "Select a slot, drag widgets into place, or use the library buttons for faster assignment." }
                </Text>
              </Space>
            </div>

            <div style={ builderStyles.workspaceBody }>
              <Space direction="vertical" size={ 16 } style={ { width: "100%" } }>
                { rows.length === 0 ? (
                  <Empty description="No rows configured yet." />
                ) : rows.map((row, rowIndex) => (
                  <DashboardRowCard
                    key={ `dashboard-row-${rowIndex}` }
                    row={ row }
                    rowIndex={ rowIndex }
                    widgetsById={ widgetsById }
                    disabled={ disabled }
                    selectedSlot={ selectedSlot }
                    onSelectSlot={ setSelectedSlot }
                    onDropWidget={ handleWidgetPlacement }
                    onClearWidget={ onClearWidget }
                    onMoveRow={ onMoveRow }
                    onRemoveRow={ onRemoveRow }
                    onSplitColumn={ onSplitColumn }
                    onOpenPicker={ openSlotPicker }
                  />
                )) }

                {!disabled && (
                  <div style={ { display: "flex", justifyContent: "center", paddingTop: 8 } }>
                    <CreateButton
                      onClick={ onAddRow }
                      text="Add Row"
                      icon={ <PlusOutlined /> }
                      style={ { minWidth: 160, height: 42, borderRadius: 10 } }
                    />
                  </div>
                ) }
              </Space>
            </div>
          </Card>
        </Col>
      </Row>

      {!disabled && (
        <Modal
          title={ selectedSlot ? `Choose widget for Row ${selectedSlot.rowIndex + 1}, Slot ${selectedSlot.colIndex + 1}` : "Choose widget" }
          open={ pickerOpen }
          onCancel={ () => setPickerOpen(false) }
          footer={ null }
          width={ 760 }
        >
          <Space direction="vertical" size={ 16 } style={ { width: "100%" } }>
            <Input
              allowClear
              value={ searchValue }
              onChange={ (event) => setSearchValue(event.target.value) }
              placeholder="Search widgets"
              prefix={ <SearchOutlined style={ { color: "#8ea0b7" } } /> }
            />

            <div style={ { maxHeight: 420, overflowY: "auto", paddingRight: 4 } }>
              <Row gutter={ [12, 12] }>
                { visibleWidgets.length === 0 && (
                  <Col span={ 24 }>
                    <Empty image={ Empty.PRESENTED_IMAGE_SIMPLE } description="No widgets match your search." />
                  </Col>
                ) }

                { visibleWidgets.map((widget) => (
                  <Col xs={ 24 } sm={ 12 } key={ `picker-${widget.id}` }>
                    <div
                      style={ {
                        ...builderStyles.widgetCard,
                        borderColor: assignedWidgetIds.has(widget.id) ? "#9ec5ff" : "#d7e3f4",
                        background: assignedWidgetIds.has(widget.id) ? "#f7fbff" : "#fff",
                      } }
                    >
                      <Space direction="vertical" size={ 10 } style={ { width: "100%" } }>
                        <Space align="start" style={ { width: "100%", justifyContent: "space-between" } }>
                          <Space direction="vertical" size={ 4 } style={ { flex: 1 } }>
                            <Text strong style={ { color: "#21324d" } }>{ widget.name }</Text>
                            <Text type="secondary" style={ { fontSize: 12 } }>
                              { getWidgetCopy(widget) }
                            </Text>
                          </Space>
                          { assignedWidgetIds.has(widget.id) && <Tag color="blue">Assigned</Tag> }
                        </Space>

                        <Button type="primary" onClick={ () => handleAssignFromPicker(widget.id) } block>
                          { assignedWidgetIds.has(widget.id) ? "Move to this slot" : "Use this widget" }
                        </Button>
                      </Space>
                    </div>
                  </Col>
                )) }
              </Row>
            </div>
          </Space>
        </Modal>
      ) }
    </div>
  );
};

export default CustomDashboardBuilder;
